import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Layers } from "react-feather";

const ALL_PRODUCTS = "All Products";

const getCategoryId = (category, index) =>
  category?.category_id || category?.id || category?.code || category?.name || `category-${index}`;

const CategoryTabs = ({ categories = [], selectedCategory, setSelectedCategory }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const safeCategories = useMemo(
    () => (Array.isArray(categories) ? categories.filter(Boolean) : []),
    [categories]
  );

  const activeCategory = selectedCategory || ALL_PRODUCTS;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollLeft = el.scrollLeft;
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);

    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft < maxScrollLeft - 4);
  }, []);

  useEffect(() => {
    updateScrollState();

    const el = scrollRef.current;
    if (!el) return undefined;

    const handleScroll = () => updateScrollState();
    const handleResize = () => updateScrollState();

    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [safeCategories.length, updateScrollState]);

  const scrollByAmount = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const amount = Math.max(220, Math.round(el.clientWidth * 0.55));
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const handleSelect = (categoryName) => {
    if (typeof setSelectedCategory === "function") {
      setSelectedCategory(categoryName);
    }
  };

  const renderTab = ({ key, label, isActive, isAll = false }) => (
    <button
      key={key}
      type="button"
      className={`ns-category-tab ${isActive ? "active" : ""} ${isAll ? "all-products" : ""}`}
      onClick={() => handleSelect(label)}
      aria-pressed={isActive}
      title={label}
    >
      {isAll ? (
        <span className="ns-category-tab-icon">
          <Layers size={14} />
        </span>
      ) : null}
      <span className="ns-category-tab-label">{label}</span>
    </button>
  );

  return (
    <div
      className={`ns-category-tabs-shell ${canScrollLeft ? "has-left" : ""} ${
        canScrollRight ? "has-right" : ""
      }`}
    >
      <style>{`
        .ns-category-tabs-shell {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }

        .ns-category-scroll {
          position: relative;
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 9px;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          white-space: nowrap;
          scrollbar-width: none;
          padding: 2px 2px;
          -webkit-overflow-scrolling: touch;
        }

        .ns-category-scroll::-webkit-scrollbar {
          display: none;
        }

        .ns-category-tabs-shell::before,
        .ns-category-tabs-shell::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          width: 44px;
          z-index: 3;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.16s ease;
        }

        .ns-category-tabs-shell::before {
          left: 40px;
          background: linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%);
        }

        .ns-category-tabs-shell::after {
          right: 40px;
          background: linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%);
        }

        .ns-category-tabs-shell.has-left::before {
          opacity: 1;
        }

        .ns-category-tabs-shell.has-right::after {
          opacity: 1;
        }

        .ns-category-nav {
          width: 36px;
          height: 36px;
          border-radius: 14px;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: #ffffff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          cursor: pointer;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.07);
          transition: all 0.16s ease;
          z-index: 4;
        }

        .ns-category-nav:hover:not(:disabled) {
          background: #eff6ff;
          color: #1d4ed8;
          transform: translateY(-1px);
          box-shadow: 0 12px 26px rgba(37, 99, 235, 0.14);
        }

        .ns-category-nav:disabled {
          opacity: 0.42;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .ns-category-tab {
          position: relative;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 999px;
          min-height: 42px;
          padding: 0 18px;
          background: #f1f5f9;
          color: #334155;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 0.84rem;
          font-weight: 850;
          cursor: pointer;
          flex: 0 0 auto;
          box-shadow: 0 7px 18px rgba(15, 23, 42, 0.04);
          transition:
            background 0.16s ease,
            color 0.16s ease,
            border-color 0.16s ease,
            box-shadow 0.16s ease,
            transform 0.16s ease;
          outline: none;
        }

        .ns-category-tab:hover {
          background: #ffffff;
          border-color: rgba(37, 99, 235, 0.24);
          color: #1d4ed8;
          transform: translateY(-1px);
          box-shadow: 0 11px 24px rgba(15, 23, 42, 0.08);
        }

        .ns-category-tab:focus-visible {
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
        }

        .ns-category-tab.active {
          background: linear-gradient(135deg, #1d4ed8 0%, #0d6efd 100%);
          border-color: #1d4ed8;
          color: #ffffff;
          box-shadow: 0 12px 26px rgba(37, 99, 235, 0.24);
        }

        .ns-category-tab.active::after {
          content: "";
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: -7px;
          height: 3px;
          border-radius: 999px;
          background: #22c55e;
          box-shadow: 0 4px 10px rgba(34, 197, 94, 0.22);
        }

        .ns-category-tab.all-products {
          padding-left: 14px;
        }

        .ns-category-tab-icon {
          width: 24px;
          height: 24px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.20);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .ns-category-tab:not(.active) .ns-category-tab-icon {
          background: #ffffff;
          color: #2563eb;
        }

        .ns-category-tab-label {
          max-width: 170px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 768px) {
          .ns-category-tabs-shell {
            gap: 6px;
          }

          .ns-category-nav {
            width: 34px;
            height: 34px;
            border-radius: 13px;
          }

          .ns-category-tab {
            min-height: 38px;
            padding: 0 14px;
            font-size: 0.78rem;
          }

          .ns-category-tab-label {
            max-width: 132px;
          }

          .ns-category-tabs-shell::before {
            left: 38px;
          }

          .ns-category-tabs-shell::after {
            right: 38px;
          }
        }
      `}</style>

      <button
        type="button"
        className="ns-category-nav"
        onClick={() => scrollByAmount("left")}
        disabled={!canScrollLeft}
        aria-label="Scroll categories left"
      >
        <ChevronLeft size={18} />
      </button>

      <div ref={scrollRef} className="ns-category-scroll pos-category-tabs">
        {renderTab({
          key: ALL_PRODUCTS,
          label: ALL_PRODUCTS,
          isActive: activeCategory === ALL_PRODUCTS,
          isAll: true,
        })}

        {safeCategories.map((category, index) =>
          renderTab({
            key: getCategoryId(category, index),
            label: category?.name || `Category ${index + 1}`,
            isActive: activeCategory === category?.name,
          })
        )}
      </div>

      <button
        type="button"
        className="ns-category-nav"
        onClick={() => scrollByAmount("right")}
        disabled={!canScrollRight}
        aria-label="Scroll categories right"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

export default CategoryTabs;
